import { useCallback, useMemo, useRef, useState } from 'react';
import { colors } from '@ardoq/design-tokens';
import { formatNumber, type Locale } from '@ardoq/locale';
import { resolveCssColor } from '@ardoq/xyz-charts';

type CreateExportCanvas = () => OffscreenCanvas | null;

export type RadialChartHost = {
  setContainer: (element: HTMLDivElement | null) => void;
  container: HTMLDivElement | null;
  chartColors: {
    surfaceColor: string;
    inkColor: string;
    tooltipBorderColor: string;
  };
  createExportCanvas: CreateExportCanvas;
  onExportCanvasAvailable: (
    createExportCanvas: CreateExportCanvas | null
  ) => void;
  format: (value: number) => string;
};

/** The theme colors, export hook and number format both radial views hand their chart. */
export const useRadialChartHost = (locale: Locale): RadialChartHost => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const chartColors = useMemo(
    () => ({
      surfaceColor: resolveCssColor(
        container,
        colors.surfaceDefault,
        '#ffffff'
      ),
      inkColor: resolveCssColor(container, colors.textDefault, '#1F2126'),
      tooltipBorderColor: resolveCssColor(
        container,
        colors.borderSubtle,
        '#DADADA'
      ),
    }),
    [container]
  );

  const createExportCanvasRef = useRef<CreateExportCanvas | null>(null);
  const onExportCanvasAvailable = useCallback(
    (createExportCanvas: CreateExportCanvas | null) => {
      createExportCanvasRef.current = createExportCanvas;
    },
    []
  );
  const createExportCanvas = useCallback(
    () => createExportCanvasRef.current?.() ?? null,
    []
  );

  const format = useCallback(
    (value: number) => formatNumber(value, 'default', locale),
    [locale]
  );

  return {
    setContainer,
    container,
    chartColors,
    createExportCanvas,
    onExportCanvasAvailable,
    format,
  };
};
